import React from 'react';

const Section = ({ number, title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-semibold text-gray-900 mb-4">{number}. {title}</h2>
    {children}
  </section>
);

const P = ({ children }) => (
  <p className="text-gray-700 leading-relaxed mb-3">{children}</p>
);

const Ul = ({ items }) => (
  <ul className="list-disc pl-6 text-gray-700 mb-3 space-y-1">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);

const Terms = () => (
  <div className="min-h-screen bg-white">
    <nav className="border-b border-gray-200 bg-white">
      <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 stripe-gradient rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M4 3a2 2 0 00-2 2v1a1 1 0 001 1h14a1 1 0 001-1V5a2 2 0 00-2-2H4zM3 8a1 1 0 011-1h12a1 1 0 011 1v5a2 2 0 01-2 2H6a2 2 0 01-2-2V8z" />
            </svg>
          </div>
          <span className="text-2xl font-semibold text-gray-900">Journal Coffee</span>
        </div>
        <a href="/" className="text-gray-600 hover:text-gray-900 font-medium">← Back to Home</a>
      </div>
    </nav>

    <main className="max-w-3xl mx-auto px-6 py-12">
      <h1 className="text-4xl font-bold text-gray-900 mb-3">Terms of Service</h1>
      <p className="text-gray-500 mb-10">Last updated: June 10, 2026</p>

      <div>
        <Section number="1" title="Acceptance of Terms">
          <P>
            By creating an account or using Journal Coffee ("the Service"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.
          </P>
        </Section>

        <Section number="2" title="Service Description">
          <P>
            Journal Coffee provides access to AI-generated summaries of academic and research papers, delivered as written articles and podcast-style audio recordings. Content is curated and published on a regular basis; we do not guarantee a fixed publication schedule.
          </P>
          <P>
            Access to the full content library requires an active paid subscription or an ongoing free trial. A valid payment method must be on file to use the Service.
          </P>
        </Section>

        <Section number="3" title="Free Trial">
          <P>
            New subscribers receive a 7-day free trial when they first sign up for a paid plan. The following conditions apply:
          </P>
          <Ul items={[
            'A valid credit or debit card is required to start the trial. No charge is made until the trial ends.',
            'If you cancel before the trial period ends, you will not be charged.',
            'If you do not cancel, your chosen plan will automatically begin billing at the end of the 7-day period.',
            'The free trial is available once per customer. Creating multiple accounts to obtain additional trials is not permitted.',
          ]} />
        </Section>

        <Section number="4" title="Subscription Plans and Billing">
          <P>Journal Coffee offers two subscription plans:</P>
          <Ul items={[
            'Monthly — $6.00 per month, billed monthly',
            'Annual — $50.00 per year, billed annually (~$4.17/month)',
          ]} />
          <P>
            Subscriptions renew automatically at the end of each billing period using the payment method on file. Prices shown are in USD and may be subject to applicable taxes.
          </P>
          <P>
            We reserve the right to change pricing for new subscribers with reasonable advance notice. Existing subscribers will be notified at least 30 days before any price change affecting their plan.
          </P>
        </Section>

        <Section number="5" title="Cancellation">
          <P>
            You may cancel your subscription at any time by contacting us at <a href="mailto:support@journalcoffee.com" className="text-indigo-600 hover:underline">support@journalcoffee.com</a>. Cancellation takes effect at the end of your current billing period — you will retain access until then.
          </P>
          <P>
            To cancel during your free trial and avoid being charged, contact us before the trial end date shown in your Account page.
          </P>
        </Section>

        <Section number="6" title="Refunds">
          <P>
            We offer a 30-day money-back guarantee for first-time subscribers after the trial period ends. If you are not satisfied within 30 days of your first charge, contact <a href="mailto:support@journalcoffee.com" className="text-indigo-600 hover:underline">support@journalcoffee.com</a> for a full refund.
          </P>
          <P>
            Refunds are not available for renewals after the initial billing period or for cancellations mid-period after the refund window has closed.
          </P>
        </Section>

        <Section number="7" title="Acceptable Use">
          <P>You agree not to:</P>
          <Ul items={[
            'Share your account credentials or allow others to access the Service using your account',
            'Download, copy, redistribute, or resell any content from the Service for commercial purposes',
            'Use automated tools to scrape, download, or bulk-access content',
            'Attempt to circumvent access controls or security measures',
            'Use the Service in any way that violates applicable laws or regulations',
          ]} />
          <P>
            Violation of these terms may result in immediate suspension or termination of your account without refund.
          </P>
        </Section>

        <Section number="8" title="Intellectual Property">
          <P>
            All content on Journal Coffee — including article text, audio summaries, and supporting materials — is owned by or licensed to Journal Coffee. You are granted a limited, non-exclusive, non-transferable license to access and use the content for personal, non-commercial purposes while your subscription is active.
          </P>
          <P>
            Research papers summarised on the Service are the intellectual property of their respective authors and publishers. Journal Coffee summaries are original works and do not reproduce the source papers in full.
          </P>
        </Section>

        <Section number="9" title="Third-Party Services">
          <P>The Service relies on the following third-party providers:</P>
          <Ul items={[
            'Stripe — payment processing. By subscribing you also agree to Stripe\'s terms of service.',
            'Google Firebase — authentication, database, and file storage.',
            'Algolia — search functionality.',
            'Amazon Web Services — content delivery.',
          ]} />
          <P>
            We are not responsible for the availability or conduct of these third-party services.
          </P>
        </Section>

        <Section number="10" title="Disclaimer of Warranties">
          <P>
            The Service is provided "as is" without warranties of any kind, express or implied. We do not guarantee that content is error-free, complete, or up to date. Research summaries are for informational purposes and should not be relied upon as a substitute for the original source material.
          </P>
        </Section>

        <Section number="11" title="Limitation of Liability">
          <P>
            To the maximum extent permitted by law, Journal Coffee shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of data or interruption of service, arising from your use of or inability to use the Service.
          </P>
        </Section>

        <Section number="12" title="Changes to These Terms">
          <P>
            We may update these Terms from time to time. Material changes will be communicated by email or by a notice on the Service at least 14 days before they take effect. Continued use of the Service after changes take effect constitutes acceptance of the updated Terms.
          </P>
        </Section>

        <Section number="13" title="Contact">
          <P>
            For questions about these Terms, contact us at <a href="mailto:support@journalcoffee.com" className="text-indigo-600 hover:underline">support@journalcoffee.com</a>.
          </P>
        </Section>
      </div>
    </main>

    <footer className="border-t border-gray-100 py-8 px-6 text-center">
      <div className="flex justify-center gap-6 text-sm text-gray-500">
        <a href="/privacy" className="hover:text-gray-700 transition-colors">Privacy Policy</a>
        <a href="/" className="hover:text-gray-700 transition-colors">Home</a>
      </div>
    </footer>
  </div>
);

export default Terms;
